import apiService from './api';

class AuthService {
    // Login con credenciales
    async login(email, password) {
        try {
            // Backend espera "contrasenia" según especificación
            const response = await apiService.post('/auth/login', {
                email,
                contrasenia: password
            });

            if (response.token) {
                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user_data', JSON.stringify(response.user));
            }

            return response;
        } catch (error) {
            throw new Error(error.message || 'Error en el login');
        }
    }

    // Registro de nuevo usuario
    async register(userData) {
        try {
            // Asegurar nombres de campos en español
            const payload = {
                nombre: userData.nombre,
                apellido: userData.apellido,
                email: userData.email,
                telefono: userData.telefono,
                contrasenia: userData.contrasenia
            };
            const response = await apiService.post('/auth/register', payload);
            
            if (response.token) {
                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user_data', JSON.stringify(response.user));
            }

            return response;
        } catch (error) {
            throw new Error(error.message || 'Error en el registro');
        }
    }

    // Login social (Google, Facebook)
    async socialLogin(provider, token) {
        try {
            const normalizedProvider = (provider || '').toLowerCase();
            const socialPath = (process.env.REACT_APP_SOCIAL_LOGIN_PATH || (typeof window !== 'undefined' && window.__APP_CONFIG__?.SOCIAL_LOGIN_PATH) || '/auth/social');

            const payload = {
                // Campos esperados por nuestro backend
                provider: normalizedProvider,
                token,
                // Campos alternativos para compatibilidad con backends existentes
                proveedor: normalizedProvider,
                access_token: token,
                id_token: token
            };

            const response = await apiService.post(socialPath, payload);

            if (response.token) {
                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user_data', JSON.stringify(response.user));
            }

            return response;
        } catch (error) {
            throw new Error(error.message || 'Error en login social');
        }
    }

    // Verificar token
    async verifyToken() {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                throw new Error('No token found');
            }
            const response = await apiService.get('/auth/verify');
            return response;
        } catch (error) {
            this.logout();
            throw new Error('Token inválido');
        }
    }

    // Obtener perfil del usuario (\api\auth\me)
    async me() {
        try {
            return await apiService.get('/auth/me');
        } catch (error) {
            throw new Error(error.message || 'Error al validar sesión');
        }
    }

    // Actualizar perfil
    async updateProfile(profileData) {
        try {
            const response = await apiService.put('/auth/profile', profileData);
            
            // Actualizar datos locales
            const currentUser = JSON.parse(localStorage.getItem('user_data') || '{}');
            const updatedUser = { ...currentUser, ...response.user };
            localStorage.setItem('user_data', JSON.stringify(updatedUser));

            return response;
        } catch (error) {
            throw new Error(error.message || 'Error al actualizar perfil');
        }
    }

    // Cambiar contraseña
    async changePassword(currentPassword, newPassword) {
        try {
            return await apiService.put('/auth/change-password', {
                currentPassword,
                newPassword
            });
        } catch (error) {
            throw new Error(error.message || 'Error al cambiar contraseña');
        }
    }

    // Solicitar recuperación de contraseña
    async requestPasswordReset(email) {
        try {
            return await apiService.post('/auth/forgot-password', { email });
        } catch (error) {
            throw new Error(error.message || 'Error al solicitar recuperación');
        }
    }

    // Restablecer contraseña
    async resetPassword(token, newPassword) {
        try {
            return await apiService.post('/auth/reset-password', {
                token,
                newPassword
            });
        } catch (error) {
            throw new Error(error.message || 'Error al restablecer contraseña');
        }
    }

    // Logout
    logout() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        
        // Opcional: notificar al servidor
        apiService.post('/auth/logout').catch(() => {
            // Ignorar errores en logout
        });
    }

    // Verificar si el usuario está autenticado
    isAuthenticated() {
        const token = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');
        return !!(token && userData);
    }

    // Obtener datos del usuario desde localStorage
    getCurrentUser() {
        try {
            const userData = localStorage.getItem('user_data');
            return userData ? JSON.parse(userData) : null;
        } catch (error) {
            console.error('Error parsing user data:', error);
            return null;
        }
    }

    // Obtener token actual
    getToken() {
        return localStorage.getItem('auth_token');
    }

    // Refrescar token
    async refreshToken() {
        try {
            const response = await apiService.post('/auth/refresh');
            
            if (response.token) {
                localStorage.setItem('auth_token', response.token);
            }

            return response;
        } catch (error) {
            this.logout();
            throw new Error('Error al refrescar token');
        }
    }
}

// Instancia singleton del servicio de autenticación
const authService = new AuthService();

export default authService;
